// Family licences — one licence gives one family full app access, paid by the
// organisation (migration 20261022000008; replaces "Seats & licence" and
// "Sponsored families": staff seats are free now, so the only thing an org
// manages commercially is family licences).
//
// THE CHECK COMES FIRST. Giving a licence starts with check_family_licence,
// whose answer is shown in plain words BEFORE "Give licence" is enabled: a
// family that already pays in the app store needs none, a family this org
// already licenses needs no second one, and a suspended org cannot give any.
// The server refuses all three anyway; the check is what makes the refusal a
// sentence instead of a surprise.
//
// AN ALLOWANCE OF ZERO IS FIRST-CLASS. An org (or a single practitioner) may
// run with no licences at all and still link to children and send practice
// suggestions; the screen says so calmly and shows no form.
//
// Adult emails and dates only. No child appears here.

import { useMemo, useState } from "react";
import { useComfort } from "../ComfortContext";
import { fill, getDictionary } from "../../i18n/dictionary";
import { getPortalData, notifyFamilyInBackground, shouldUseSupabase } from "../../data";
import { DEMO_NOW_MS } from "../../data/fixturesDemo";
import type { FamilyLicenceCheck } from "../../data/types";
import {
  countsTowardAllowance,
  sponsorshipStatus,
  type Sponsorship,
  type SponsorshipStatus,
} from "../../domain/sponsorship";
import { useOrg } from "./OrgContext";
import { BulkEndDate, BulkUpload, FixEmailDialog } from "./BulkLicences";
import { useOrgProfile } from "./orgProfile";
import {
  Button,
  Card,
  Chip,
  ConfirmDialog,
  ErrorPanel,
  Field,
  GRID_TILES,
  LoadingLine,
  Notice,
  PageHeader,
  StatTile,
  TableCard,
  TD,
  TH,
  endOfDay,
  errorCode,
  formatDate,
  looksLikeEmail,
  todayIso,
  useDemoVariant,
  useLoad,
  type Tone,
} from "./ui";

const DAY = 24 * 60 * 60 * 1000;
/** Row checkboxes: a 24px box inside a 44px cell, so the target stays generous. */
const CHECKBOX = { width: 24, height: 24, margin: 0, accentColor: "var(--brand)", cursor: "pointer" } as const;
const ENDING_SOON_DAYS = 30;

type T = ReturnType<typeof getDictionary>["admin"]["licences"];

/** The plain-words answer for a check result. */
export function checkSentence(t: T, check: FamilyLicenceCheck): string {
  switch (check.status) {
    case "paid":
      return t.checkPaid;
    case "licensed":
      return t.checkLicensed;
    case "available":
      return fill(t.checkAvailable, { n: check.remaining });
    case "full":
      return t.checkFull;
    case "no_allowance":
      return t.checkNoAllowance;
    case "suspended":
      return t.checkSuspended;
  }
}

/** A refused write → calm copy. SQLSTATE first; P0001 carries the rule that refused. */
export function licenceErrorCopy(t: T, err: unknown): string {
  const code = errorCode(err);
  if (code === "42501") return t.errNotAuth;
  if (code === "P0001") {
    const msg = err instanceof Error ? err.message.toLowerCase() : "";
    if (msg.includes("app store")) return t.checkPaid;
    if (msg.includes("suspended")) return t.checkSuspended;
    if (msg.includes("already has a sponsorship")) return t.checkLicensed;
    if (msg.includes("allowance reached")) return t.checkFull;
    if (msg.includes("no family sponsorships")) return t.checkNoAllowance;
    return t.errRefused;
  }
  return t.errGeneric;
}

function statusChip(t: T, status: SponsorshipStatus): { tone: Tone; label: string } {
  switch (status) {
    case "active":
      return { tone: "good", label: t.statusActive };
    case "pending":
      return { tone: "neutral", label: t.statusPending };
    case "lapsed":
      return { tone: "muted", label: t.statusLapsed };
    case "revoked":
      return { tone: "muted", label: t.statusRevoked };
  }
}

function defaultEndDate(nowMs: number): string {
  return todayIso(nowMs + 365 * DAY);
}

export function FamilyLicences() {
  const { locale } = useComfort();
  const t = getDictionary(locale).admin.licences;
  const c = getDictionary(locale).common;
  const { orgId } = useOrg();
  const profile = useOrgProfile();
  const variant = useDemoVariant();
  const live = shouldUseSupabase();
  // The demo's dates are built around its own day (fixturesDemo.ts).
  const nowMs = live ? Date.now() : DEMO_NOW_MS;

  const load = useLoad(async () => {
    const ds = getPortalData();
    const [rows, allowance] = await Promise.all([ds.listSponsorships(orgId), ds.getFamilyAllowance(orgId)]);
    return { rows, allowance };
  }, [orgId, variant]);

  const [email, setEmail] = useState("");
  const [endsOn, setEndsOn] = useState(() => defaultEndDate(Date.now()));
  const [formError, setFormError] = useState("");
  const [check, setCheck] = useState<{ email: string; result: FamilyLicenceCheck } | null>(null);
  const [busy, setBusy] = useState<"" | "check" | "give">("");
  const [notice, setNotice] = useState("");
  const [showPast, setShowPast] = useState(false);
  const [editing, setEditing] = useState<{ id: string; date: string; error: string } | null>(null);
  const [ending, setEnding] = useState<Sponsorship | null>(null);
  const [endBusy, setEndBusy] = useState(false);
  const [endError, setEndError] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [before, setBefore] = useState("");
  const [fixing, setFixing] = useState<Sponsorship | null>(null);

  const rows = useMemo(() => load.data?.rows ?? [], [load.data]);
  const allowance = load.data?.allowance ?? 0;
  const liveRows = rows.filter((r) => countsTowardAllowance(r, nowMs));
  const pastRows = rows.filter((r) => !countsTowardAllowance(r, nowMs));
  const claimed = liveRows.filter((r) => sponsorshipStatus(r, nowMs) === "active").length;
  const remaining = Math.max(allowance - liveRows.length, 0);
  const suspended = profile.status !== "active";
  const cleanEmail = email.trim().toLowerCase();
  const checkIsCurrent = check !== null && check.email === cleanEmail;
  const canGive = checkIsCurrent && check?.result.status === "available" && !suspended && busy === "";

  function validate(): boolean {
    if (!looksLikeEmail(cleanEmail)) {
      setFormError(t.emailInvalid);
      return false;
    }
    if (!endsOn || Date.parse(endOfDay(endsOn)) <= Date.now()) {
      setFormError(t.dateInvalid);
      return false;
    }
    setFormError("");
    return true;
  }

  async function runCheck() {
    if (busy || !validate()) return;
    setBusy("check");
    setNotice("");
    try {
      const result = await getPortalData().checkFamilyLicence(orgId, cleanEmail);
      setCheck({ email: cleanEmail, result });
    } catch (err) {
      setCheck(null);
      setFormError(licenceErrorCopy(t, err));
    } finally {
      setBusy("");
    }
  }

  async function give() {
    if (!canGive || !validate()) return;
    setBusy("give");
    try {
      const sponsorshipId = await getPortalData().grantSponsorship(orgId, cleanEmail, endOfDay(endsOn));
      // Tell the family's phone, if it has one signed in. Fire-and-forget: the
      // licence is already given, so a push failure is only logged.
      notifyFamilyInBackground({ kind: "licence", sponsorshipId });
      setNotice(fill(t.givenOk, { email: cleanEmail }));
      setEmail("");
      setCheck(null);
      setEndsOn(defaultEndDate(Date.now()));
      await load.refresh();
    } catch (err) {
      setFormError(licenceErrorCopy(t, err));
    } finally {
      setBusy("");
    }
  }

  async function saveDate() {
    if (!editing) return;
    if (!editing.date || Date.parse(endOfDay(editing.date)) <= Date.now()) {
      setEditing({ ...editing, error: t.dateInvalid });
      return;
    }
    try {
      await getPortalData().extendSponsorship(editing.id, endOfDay(editing.date));
      setEditing(null);
      setNotice(t.dateSaved);
      await load.refresh();
    } catch (err) {
      setEditing({ ...editing, error: licenceErrorCopy(t, err) });
    }
  }

  async function confirmEnd() {
    if (!ending || endBusy) return;
    setEndBusy(true);
    setEndError("");
    try {
      await getPortalData().revokeSponsorship(ending.id);
      setEnding(null);
      setNotice(t.endedOk);
      await load.refresh();
    } catch (err) {
      setEndError(licenceErrorCopy(t, err));
    } finally {
      setEndBusy(false);
    }
  }

  const header = <PageHeader title={t.title} subtitle={t.subtitle} />;

  if (load.loading && !load.data) {
    return (
      <>
        {header}
        <LoadingLine label={t.loading} />
      </>
    );
  }
  if (load.error) {
    return (
      <>
        {header}
        <div style={{ marginTop: 20 }}>
          <ErrorPanel title={t.errorTitle} message={licenceErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.retry} />
        </div>
      </>
    );
  }

  const visible = showPast ? [...liveRows, ...pastRows] : liveRows;
  // Any row not ended early can have its date moved (a lapsed one is re-opened,
  // within the allowance — set_sponsorship_end_dates checks that).
  const selectable = visible.filter((r) => !r.revokedAt);
  const selectedRows = rows.filter((r) => selected.has(r.id) && !r.revokedAt);
  const allShownSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id));

  function toggle(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectEndingBefore() {
    if (!before) return;
    const cut = Date.parse(endOfDay(before));
    setSelected(new Set(liveRows.filter((r) => Date.parse(r.endsAt) <= cut).map((r) => r.id)));
  }

  return (
    <>
      {header}
      <p style={{ margin: "10px 0 0", fontSize: 14, color: "var(--ink-soft)" }}>{t.chargeNote}</p>

      <div role="status" aria-live="polite" style={{ marginTop: notice ? 16 : 0 }}>
        {notice && <Notice tone="good">{notice}</Notice>}
      </div>

      {allowance === 0 ? (
        <div style={{ marginTop: 20 }}>
          <Notice tone="info" title={t.noAllowanceTitle}>
            {t.noAllowanceBody}
          </Notice>
        </div>
      ) : (
        <>
          <div style={{ ...GRID_TILES, marginTop: 20 }}>
            <StatTile label={t.allowance} value={allowance} />
            <StatTile label={t.given} value={liveRows.length} />
            <StatTile label={t.claimed} value={claimed} />
            <StatTile label={t.pending} value={liveRows.length - claimed} />
            <StatTile label={t.remaining} value={remaining} />
          </div>

          <Card title={t.formTitle} id="give-licence" style={{ marginTop: 20 }}>
            {suspended && (
              <div style={{ marginBottom: 14 }}>
                <Notice tone="attention">{t.checkSuspended}</Notice>
              </div>
            )}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <Field
                id="licence-email"
                label={t.emailLabel}
                type="email"
                autoComplete="off"
                value={email}
                disabled={suspended}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setFormError("");
                }}
                style={{ flex: "1 1 240px" }}
              />
              <Field
                id="licence-ends"
                label={t.endsLabel}
                type="date"
                min={todayIso()}
                value={endsOn}
                disabled={suspended}
                onChange={(e) => setEndsOn(e.target.value)}
                style={{ flex: "0 1 190px" }}
              />
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Button variant="secondary" onClick={() => void runCheck()} disabled={suspended || busy !== "" || cleanEmail === ""}>
                  {busy === "check" ? t.checking : t.checkButton}
                </Button>
                <Button onClick={() => void give()} disabled={!canGive}>
                  {busy === "give" ? t.giving : t.giveButton}
                </Button>
              </div>
            </div>
            <div role="status" aria-live="polite">
              {formError && (
                <p style={{ margin: "12px 0 0", color: "var(--peach-deep)", fontWeight: 600, fontSize: 14 }}>{formError}</p>
              )}
              {checkIsCurrent && check && !formError && (
                <p
                  data-testid="licence-check"
                  style={{
                    margin: "12px 0 0",
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: check.result.status === "available" ? "var(--sage-deep)" : "var(--ink)",
                  }}
                >
                  {checkSentence(t, check.result)}
                </p>
              )}
            </div>
            {!live && <p style={{ margin: "12px 0 0", fontSize: 12.5, color: "var(--ink-faint)" }}>{t.demoPaidHint}</p>}
          </Card>

          <BulkUpload
            orgId={orgId}
            suspended={suspended}
            remaining={remaining}
            live={live}
            defaultEnds={defaultEndDate(Date.now())}
            onGiven={() => load.refresh()}
          />
        </>
      )}

      {(rows.length > 0 || allowance > 0) && (
        <section style={{ marginTop: 24 }} aria-labelledby="licence-table-title">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
            <h2 id="licence-table-title" style={{ fontSize: 18, color: "var(--ink-deep)", margin: 0 }}>
              {t.tableTitle}
            </h2>
            {pastRows.length > 0 && (
              <Button variant="quiet" onClick={() => setShowPast((v) => !v)} aria-expanded={showPast}>
                {showPast ? t.hidePast : fill(t.showPast, { n: pastRows.length })}
              </Button>
            )}
          </div>
          {visible.length > 0 && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
              <Field
                label={t.selectBeforeLabel}
                type="date"
                value={before}
                onChange={(e) => setBefore(e.target.value)}
                style={{ flex: "0 1 200px" }}
              />
              <Button variant="secondary" onClick={selectEndingBefore} disabled={!before}>
                {t.selectBeforeButton}
              </Button>
            </div>
          )}
          <BulkEndDate
            orgId={orgId}
            selected={selectedRows}
            suspended={suspended}
            onClear={() => setSelected(new Set())}
            onDone={async (n) => {
              setSelected(new Set());
              setNotice(fill(t.bulkEndDone, { n }));
              await load.refresh();
            }}
          />
          {visible.length === 0 ? (
            <Card>
              <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.empty}</p>
            </Card>
          ) : (
            <TableCard label={t.tableTitle} minWidth={700}>
              <thead>
                <tr>
                  <th scope="col" style={{ ...TH, width: 44 }}>
                    <input
                      type="checkbox"
                      aria-label={t.selectAllShown}
                      checked={allShownSelected}
                      disabled={selectable.length === 0}
                      onChange={() => setSelected(allShownSelected ? new Set() : new Set(selectable.map((r) => r.id)))}
                      style={CHECKBOX}
                    />
                  </th>
                  <th scope="col" style={TH}>{t.colFamily}</th>
                  <th scope="col" style={TH}>{t.colStatus}</th>
                  <th scope="col" style={TH}>{t.colEnds}</th>
                  <th scope="col" style={TH}>{t.colGiven}</th>
                  <th scope="col" style={TH}>{t.colActions}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const st = sponsorshipStatus(r, nowMs);
                  const chip = statusChip(t, st);
                  const isLive = st === "active" || st === "pending";
                  const daysLeft = Math.ceil((Date.parse(r.endsAt) - nowMs) / DAY);
                  const soon = isLive && daysLeft <= ENDING_SOON_DAYS;
                  const isEditing = editing?.id === r.id;
                  return (
                    <tr key={r.id} style={{ background: soon ? "var(--surface-warm)" : undefined }}>
                      <td style={{ ...TD, width: 44 }}>
                        {!r.revokedAt && (
                          <input
                            type="checkbox"
                            aria-label={fill(t.selectRow, { email: r.email })}
                            checked={selected.has(r.id)}
                            onChange={() => toggle(r.id)}
                            style={CHECKBOX}
                          />
                        )}
                      </td>
                      <td style={{ ...TD, fontWeight: 600, color: isLive ? "var(--ink-deep)" : "var(--ink-faint)", overflowWrap: "anywhere" }}>{r.email}</td>
                      <td style={TD}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <Chip tone={chip.tone}>{chip.label}</Chip>
                          {soon && (
                            <Chip tone="info">{daysLeft <= 0 ? t.endingToday : fill(t.endingSoon, { n: daysLeft })}</Chip>
                          )}
                        </div>
                      </td>
                      <td style={{ ...TD, whiteSpace: "nowrap" }}>{formatDate(locale, r.revokedAt ?? r.endsAt)}</td>
                      <td style={{ ...TD, whiteSpace: "nowrap", color: "var(--ink-soft)" }}>{formatDate(locale, r.grantedAt)}</td>
                      <td style={TD}>
                        {isLive && !isEditing && (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <Button
                              variant="secondary"
                              onClick={() => setEditing({ id: r.id, date: r.endsAt.slice(0, 10), error: "" })}
                            >
                              {t.changeEnd}
                            </Button>
                            <Button variant="secondary" onClick={() => setFixing(r)} disabled={suspended}>
                              {t.fixEmail}
                            </Button>
                            <Button variant="secondary" onClick={() => setEnding(r)}>
                              {t.endNow}
                            </Button>
                          </div>
                        )}
                        {isLive && isEditing && editing && (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                            <Field
                              label={fill(t.newEndLabel, { email: r.email })}
                              type="date"
                              min={todayIso()}
                              value={editing.date}
                              error={editing.error || undefined}
                              onChange={(e) => setEditing({ ...editing, date: e.target.value, error: "" })}
                              style={{ flex: "0 1 200px" }}
                            />
                            <Button onClick={() => void saveDate()}>{t.saveDate}</Button>
                            <Button variant="secondary" onClick={() => setEditing(null)}>
                              {c.cancel}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </TableCard>
          )}
        </section>
      )}

      {fixing && (
        <FixEmailDialog
          row={fixing}
          onCancel={() => setFixing(null)}
          onDone={async (newEmail) => {
            setFixing(null);
            setSelected(new Set());
            setNotice(fill(t.fixDone, { email: newEmail }));
            await load.refresh();
          }}
        />
      )}

      {ending && (
        <ConfirmDialog
          title={t.endConfirmTitle}
          body={
            <>
              <b style={{ color: "var(--ink-deep)", overflowWrap: "anywhere" }}>{ending.email}</b>
              <br />
              {t.endConfirmBody}
            </>
          }
          confirmLabel={t.endConfirmButton}
          cancelLabel={c.cancel}
          busy={endBusy}
          error={endError}
          onConfirm={() => void confirmEnd()}
          onCancel={() => {
            setEnding(null);
            setEndError("");
          }}
        />
      )}
    </>
  );
}
